/**
 * ExportPanel.jsx
 * Export options after the Chaotic Good score is revealed.
 * Four buttons: Full Device, Star Map, Full Transcript, Favorite Quote.
 * All exports use canvas-based rendering — no external libraries.
 */

import { useState, useCallback, useEffect } from 'react';

// ─── Button style ────────────────────────────────────────────────────────────

function getBtnBase(isMobile) {
  return {
    background: '#0a0a0a',
    border: '1px solid #333',
    color: '#aaa',
    fontFamily: 'inherit',
    fontSize: isMobile ? 12 : 8,
    padding: isMobile ? '10px 10px' : '6px 8px',
    cursor: 'pointer',
    textAlign: 'left',
    lineHeight: 1.4,
    transition: 'border-color 0.2s, color 0.2s',
    width: '100%',
    minHeight: isMobile ? 44 : 'auto',
  };
}

// Legacy compat — used only for non-mobile fallback
const btnBase = getBtnBase(false);

const btnHover = {
  borderColor: '#f7931a',
  color: '#f7931a',
};

const btnActiveStyle = {
  borderColor: '#00ff41',
  color: '#00ff41',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function downloadText(text, filename) {
  const blob = new Blob([text], { type: 'text/plain' });
  downloadBlob(blob, filename);
}

async function svgToCanvas(svgEl, width, height) {
  const serializer = new XMLSerializer();
  const svgStr = serializer.serializeToString(svgEl);
  const blob = new Blob([svgStr], { type: 'image/svg+xml;charset=utf-8' });
  const url = URL.createObjectURL(blob);

  const img = new Image();
  img.width = width;
  img.height = height;

  return new Promise((resolve, reject) => {
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(url);
      resolve(canvas);
    };
    img.onerror = reject;
    img.src = url;
  });
}

// ─── Export: Full Device ─────────────────────────────────────────────────────

async function exportFullDevice(score) {
  const size = 526;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');

  // Background
  ctx.fillStyle = '#0a0a0a';
  ctx.fillRect(0, 0, size, size);

  // Titlebar
  ctx.fillStyle = '#1a1a1a';
  ctx.fillRect(0, 0, size, 22);

  // Traffic lights
  const dots = [['#ff5f56', 10], ['#ffbd2e', 24], ['#27c93f', 38]];
  dots.forEach(([color, x]) => {
    ctx.beginPath();
    ctx.arc(x, 11, 4, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
  });

  // Title text
  ctx.fillStyle = '#555';
  ctx.font = '9px monospace';
  ctx.textAlign = 'center';
  ctx.fillText('D.A.D. V.0001', size / 2, 15);

  // Draw the SVG wheel if available
  const svgEl = document.querySelector('.antikythera-device svg');
  if (svgEl) {
    try {
      const wheelCanvas = await svgToCanvas(svgEl, 300, 300);
      const wheelX = (size - 300) / 2;
      const wheelY = 40;
      ctx.drawImage(wheelCanvas, wheelX, wheelY);
    } catch (e) {
      // SVG rendering failed — draw placeholder
      ctx.fillStyle = '#222';
      ctx.fillRect(113, 40, 300, 300);
      ctx.fillStyle = '#555';
      ctx.textAlign = 'center';
      ctx.fillText('[ star map ]', size / 2, 190);
    }
  }

  // Score area
  ctx.fillStyle = '#f7931a';
  ctx.font = 'bold 14px monospace';
  ctx.textAlign = 'center';
  ctx.fillText(`CHAOTIC GOOD: ${score}`, size / 2, 380);

  // Bottom branding
  ctx.fillStyle = '#333';
  ctx.font = '8px monospace';
  ctx.fillText('D.A.D. V.0001 \u2014 chaoticgood.wtf', size / 2, size - 16);

  // Border
  ctx.strokeStyle = '#333';
  ctx.lineWidth = 2;
  ctx.strokeRect(0, 0, size, size);

  // Export
  canvas.toBlob((blob) => {
    if (blob) downloadBlob(blob, 'dad-v0001-chart.webp');
  }, 'image/webp', 0.92);
}

// ─── Export: Star Map ────────────────────────────────────────────────────────

async function exportStarMap() {
  const svgEl = document.querySelector('.antikythera-device svg');
  if (!svgEl) return;

  const size = 400;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');

  // Transparent background (default canvas state)
  try {
    const wheelCanvas = await svgToCanvas(svgEl, size, size);
    ctx.drawImage(wheelCanvas, 0, 0);
  } catch (e) {
    ctx.fillStyle = '#555';
    ctx.font = '10px monospace';
    ctx.textAlign = 'center';
    ctx.fillText('Could not render star map', size / 2, size / 2);
  }

  canvas.toBlob((blob) => {
    if (blob) downloadBlob(blob, 'dad-v0001-starmap.png');
  }, 'image/png');
}

// ─── Export: Transcript ──────────────────────────────────────────────────────

function exportTranscript(messages) {
  const lines = [];
  lines.push('D.A.D. V.0001 \u2014 FULL TRANSCRIPT');
  lines.push('=' .repeat(50));
  lines.push('');

  for (const msg of messages) {
    if (msg.sender === 'bes') {
      lines.push(`BES: ${msg.text}`);
    } else if (msg.sender === 'thoth') {
      lines.push(`THOTH: ${msg.text}`);
    } else if (msg.sender === 'device') {
      lines.push(`D.A.D.: ${msg.text}`);
    } else if (msg.sender === 'swag') {
      lines.push(`SWAG: ${msg.text}`);
    } else if (msg.sender === 'player') {
      lines.push(`> ${msg.text}`);
    } else if (msg.type === 'riddle') {
      lines.push('');
      lines.push('RIDDLE:');
      msg.lines.forEach(l => lines.push(`  ${l}`));
    } else if (msg.type === 'clunk') {
      lines.push(`[CLUNK] ${msg.text || ''}`);
    } else if (msg.type === 'celebration') {
      lines.push(`*** ${msg.text} ***`);
    }
    // Skip hint/system messages
  }

  lines.push('');
  lines.push('=' .repeat(50));
  lines.push('Generated by D.A.D. V.0001 \u2014 chaoticgood.wtf');

  downloadText(lines.join('\n'), 'dad-v0001-transcript.txt');
}

// ─── Export: Favorite Quote ──────────────────────────────────────────────────

function getQuotableLines(messages) {
  const quotes = [];
  for (const msg of messages) {
    // Device descriptions (the meaty readings)
    if (msg.sender === 'device' && msg.text && msg.text.length > 60) {
      quotes.push(msg.text);
    }
    // Thoth context
    if (msg.sender === 'thoth' && msg.text && msg.text.length > 40) {
      quotes.push(msg.text);
    }
    // Bes memorable lines
    if (msg.sender === 'bes' && msg.text && msg.text.length > 30) {
      quotes.push(msg.text);
    }
    // Swag lines
    if (msg.sender === 'swag' && msg.text && msg.text.length > 20) {
      quotes.push(msg.text);
    }
  }
  return quotes;
}

function renderQuoteToCanvas(quote) {
  const width = 600;
  const height = 300;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  // Dark background
  ctx.fillStyle = '#0a0a0a';
  ctx.fillRect(0, 0, width, height);

  // Border
  ctx.strokeStyle = '#333';
  ctx.lineWidth = 2;
  ctx.strokeRect(1, 1, width - 2, height - 2);

  // Quote text — word wrap
  ctx.fillStyle = '#ccc';
  ctx.font = '13px monospace';
  ctx.textAlign = 'left';

  const maxWidth = width - 60;
  const lineHeight = 20;
  const words = quote.split(' ');
  const wrappedLines = [];
  let currentLine = '';

  for (const word of words) {
    const testLine = currentLine ? `${currentLine} ${word}` : word;
    const metrics = ctx.measureText(testLine);
    if (metrics.width > maxWidth && currentLine) {
      wrappedLines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = testLine;
    }
  }
  if (currentLine) wrappedLines.push(currentLine);

  const totalTextHeight = wrappedLines.length * lineHeight;
  const startY = (height - totalTextHeight) / 2 + 10;

  wrappedLines.forEach((line, i) => {
    ctx.fillText(line, 30, startY + i * lineHeight);
  });

  // Attribution
  ctx.fillStyle = '#f7931a';
  ctx.font = '9px monospace';
  ctx.textAlign = 'right';
  ctx.fillText('\u2014 D.A.D. V.0001', width - 20, height - 16);

  // Top accent line
  ctx.fillStyle = '#f7931a';
  ctx.fillRect(30, startY - 24, 40, 2);

  canvas.toBlob((blob) => {
    if (blob) downloadBlob(blob, 'dad-v0001-quote.png');
  }, 'image/png');
}

// ─── Component ───────────────────────────────────────────────────────────────

export default function ExportPanel({ messages, score }) {
  const [hoveredBtn, setHoveredBtn] = useState(null);
  const [showQuotes, setShowQuotes] = useState(false);
  const [quotableLines, setQuotableLines] = useState([]);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 540);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 540);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const mBtnBase = getBtnBase(isMobile);

  const handleFullDevice = useCallback(() => {
    exportFullDevice(score);
  }, [score]);

  const handleStarMap = useCallback(() => {
    exportStarMap();
  }, []);

  const handleTranscript = useCallback(() => {
    exportTranscript(messages);
  }, [messages]);

  const handleFavoriteQuote = useCallback(() => {
    const quotes = getQuotableLines(messages);
    setQuotableLines(quotes);
    setShowQuotes(true);
  }, [messages]);

  const handleQuoteSelect = useCallback((quote) => {
    renderQuoteToCanvas(quote);
    setShowQuotes(false);
  }, []);

  const getStyle = (id) => ({
    ...mBtnBase,
    ...(hoveredBtn === id ? btnHover : {}),
  });

  if (showQuotes) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ color: '#f7931a', fontSize: isMobile ? 11 : 8, marginBottom: 2 }}>
          PICK YOUR FAVORITE LINE:
        </div>
        <div style={{ maxHeight: isMobile ? 140 : 90, overflowY: 'auto' }}>
          {quotableLines.slice(0, 8).map((q, i) => (
            <div
              key={i}
              onClick={() => handleQuoteSelect(q)}
              onMouseEnter={() => setHoveredBtn(`q-${i}`)}
              onMouseLeave={() => setHoveredBtn(null)}
              style={{
                ...mBtnBase,
                fontSize: isMobile ? 10 : 7,
                padding: isMobile ? '8px 8px' : '3px 6px',
                marginBottom: 2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minHeight: isMobile ? 44 : 'auto',
                display: 'flex',
                alignItems: 'center',
                ...(hoveredBtn === `q-${i}` ? btnHover : {}),
              }}
            >
              {q.length > (isMobile ? 60 : 80) ? q.slice(0, isMobile ? 60 : 80) + '...' : q}
            </div>
          ))}
        </div>
        <div
          onClick={() => setShowQuotes(false)}
          style={{
            ...mBtnBase,
            textAlign: 'center',
            fontSize: isMobile ? 11 : 7,
            color: '#555',
            minHeight: isMobile ? 44 : 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            ...(hoveredBtn === 'back' ? { color: '#888', borderColor: '#555' } : {}),
          }}
          onMouseEnter={() => setHoveredBtn('back')}
          onMouseLeave={() => setHoveredBtn(null)}
        >
          back
        </div>
      </div>
    );
  }

  const labelSize = isMobile ? 10 : 7;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div style={{ color: '#f7931a', fontSize: isMobile ? 11 : 8, marginBottom: 2 }}>
        EXPORTS:
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: isMobile ? 6 : 4 }}>
        <div
          onClick={handleFullDevice}
          onMouseEnter={() => setHoveredBtn('device')}
          onMouseLeave={() => setHoveredBtn(null)}
          style={getStyle('device')}
        >
          <div style={{ color: hoveredBtn === 'device' ? '#f7931a' : '#666', fontSize: labelSize }}>FULL DEVICE</div>
          <div>WebP</div>
        </div>
        <div
          onClick={handleStarMap}
          onMouseEnter={() => setHoveredBtn('map')}
          onMouseLeave={() => setHoveredBtn(null)}
          style={getStyle('map')}
        >
          <div style={{ color: hoveredBtn === 'map' ? '#f7931a' : '#666', fontSize: labelSize }}>STAR MAP</div>
          <div>PNG</div>
        </div>
        <div
          onClick={handleTranscript}
          onMouseEnter={() => setHoveredBtn('transcript')}
          onMouseLeave={() => setHoveredBtn(null)}
          style={getStyle('transcript')}
        >
          <div style={{ color: hoveredBtn === 'transcript' ? '#f7931a' : '#666', fontSize: labelSize }}>FULL TRANSCRIPT</div>
          <div>TXT</div>
        </div>
        <div
          onClick={handleFavoriteQuote}
          onMouseEnter={() => setHoveredBtn('quote')}
          onMouseLeave={() => setHoveredBtn(null)}
          style={getStyle('quote')}
        >
          <div style={{ color: hoveredBtn === 'quote' ? '#f7931a' : '#666', fontSize: labelSize }}>FAVORITE QUOTE</div>
          <div>PNG</div>
        </div>
      </div>
    </div>
  );
}
